/**
 * ReportBreadcrumb
 *
 * Pages declare their breadcrumb items by rendering <ReportBreadcrumb items={...} />.
 * The component itself publishes the items into the shared BreadcrumbContext;
 * the persistent rendering happens in <BreadcrumbHost /> in the layout. This
 * keeps the breadcrumb mounted across route changes so AnimatePresence can
 * smoothly animate crumb additions/removals between pages.
 *
 * Animation behavior is unchanged — the host applies the same enter/exit + layout
 * transitions that used to live on the per-page component.
 */

import { useState, useEffect, useRef, type ReactNode } from "react";
import { Link, useNavigate, useLocation } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBreadcrumbState, useSetBreadcrumb } from "../../lib/breadcrumb";
import { getNavIcon } from "../layout/navConfig";

export interface BreadcrumbItem {
  label: string;
  to?: string;
  onClick?: () => void;
}

/** Page-side publisher — renders nothing. */
export function ReportBreadcrumb({
  items,
  trailing,
  onBeforeNavigate,
}: {
  items: BreadcrumbItem[];
  trailing?: ReactNode;
  onBeforeNavigate?: () => void;
}) {
  useSetBreadcrumb(items, { trailing, onBeforeNavigate });
  return null;
}

/**
 * Persistent breadcrumb renderer. Rendered once by the layout inside #header-breadcrumb.
 * Owns the click → animate-exit → navigate sequence and the AnimatePresence animation.
 */
export function BreadcrumbHost() {
  const { items, trailing, onBeforeNavigate } = useBreadcrumbState();
  const navigate = useNavigate();
  const location = useLocation();
  const [frozenItems, setFrozenItems] = useState<BreadcrumbItem[] | null>(null);
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  // If the URL pathname changes for a reason OTHER than a breadcrumb-driven
  // navigation (sidebar, browser back/forward, programmatic nav, etc), the
  // frozen snapshot is stale — drop it. When the change WAS triggered by the
  // breadcrumb (pendingNav was set), we let the items-match effect below clear
  // the frozen snapshot once the new page publishes its items, so the
  // freeze→nav→new-items handoff stays smooth.
  const pendingNavRef = useRef<string | null>(pendingNav);
  pendingNavRef.current = pendingNav;
  useEffect(() => {
    if (pendingNavRef.current) return;
    setFrozenItems(null);
  }, [location.pathname]);

  const handleNavClick = (e: React.MouseEvent, to: string, clickedIndex: number) => {
    e.preventDefault();
    onBeforeNavigate?.();
    setFrozenItems(items.slice(0, clickedIndex + 1));
    setPendingNav(to);
  };

  // Trigger navigation after the exit animation finishes. Keep both pendingNav
  // and frozenItems set until the new page publishes matching items — that way
  // the breadcrumb keeps showing the frozen target across the route handoff
  // and the pathname-change effect knows to leave the frozen snapshot alone.
  useEffect(() => {
    if (!pendingNav) return;
    const navTimer = setTimeout(() => {
      navigate({ to: pendingNav });
    }, 220);
    // Safety: if the new page never publishes matching items (e.g. it doesn't
    // render <ReportBreadcrumb>), drop the frozen state after ~1.5s so future
    // navigations aren't blocked.
    const safetyTimer = setTimeout(() => {
      setFrozenItems(null);
      setPendingNav(null);
    }, 1500);
    return () => {
      clearTimeout(navTimer);
      clearTimeout(safetyTimer);
    };
  }, [pendingNav, navigate]);

  // Clear frozen snapshot once the new page has published items that match
  // what we froze. This is the single source of truth for ending a
  // breadcrumb-triggered navigation.
  useEffect(() => {
    if (!frozenItems) return;
    const matches =
      items.length === frozenItems.length &&
      items.every((it, i) => it.label === frozenItems[i]!.label && (it.to ?? null) === (frozenItems[i]!.to ?? null));
    if (matches) {
      setFrozenItems(null);
      setPendingNav(null);
    }
  }, [items, frozenItems]);

  const displayItems = frozenItems ?? items;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center justify-between min-w-0">
      <motion.div
        layout
        className="flex flex-nowrap items-center gap-2 font-semibold"
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <AnimatePresence initial={false}>
          {displayItems.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === displayItems.length - 1;
            // Leading section icon on the first crumb (e.g. the Goals icon before
            // "Goals"), resolved from the shared sidebar nav config.
            const LeadIcon = isFirst ? getNavIcon(item) : null;

            return (
              <motion.div
                key={index}
                layout
                className="inline-flex items-center gap-2 whitespace-nowrap shrink-0 text-[17px] overflow-hidden"
                initial={isFirst ? false : { opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={isFirst ? undefined : { opacity: 0, x: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {!isFirst && <ChevronRight size={18} className="text-muted-foreground/40" />}
                {isFirst && LeadIcon && (
                  <LeadIcon
                    size={18}
                    className={`shrink-0 mr-0.5 ${isLast ? "text-foreground" : "text-muted-foreground"}`}
                    aria-hidden="true"
                  />
                )}
                {isLast && !isFirst ? (
                  <span className="text-foreground">{item.label}</span>
                ) : !isFirst && item.to ? (
                  <Link
                    to={item.to}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={(e) => handleNavClick(e, item.to!, index)}
                  >
                    {item.label}
                  </Link>
                ) : !isFirst && item.onClick ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={item.onClick}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") item.onClick!(); }}
                    className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer touch-target-sm"
                  >
                    {item.label}
                  </span>
                ) : isFirst && isLast ? (
                  <span className="text-foreground">{item.label}</span>
                ) : isFirst && item.to ? (
                  <Link
                    to={item.to}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={(e) => handleNavClick(e, item.to!, 0)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">{item.label}</span>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
      {trailing}
    </nav>
  );
}
